import { mdiMenuUp, mdiMenuDown } from '@mdi/js';
import { JobType } from '../redux/jobs/jobs.types';

export interface PlausibilityIndicator {
    path: string;
    tooltipKey: string;
    /** Wording for the text variant (config.features.showPlausibilityAsText). */
    textKey: string;
}

const HIGHER_THAN_EXPECTED: PlausibilityIndicator = {
    path: mdiMenuUp,
    tooltipKey: 'jobs.details.tooltips.readingValueGreaterThanMaximumValue',
    textKey: 'jobs.details.plausibilityHints.readingValueGreaterThanMaximumValue',
};

const LOWER_THAN_EXPECTED: PlausibilityIndicator = {
    path: mdiMenuDown,
    tooltipKey: 'jobs.details.tooltips.readingValueLessThanMinimumValue',
    textKey: 'jobs.details.plausibilityHints.readingValueLessThanMinimumValue',
};

/**
 * Resolves the plausibility indicator (arrow up/down) for a single recorded reading value
 * against its expected range. Returns undefined when the value is plausible (or missing).
 *
 * Shared by MeterReadingPlausibilityIndicator (detail/success screen) and the job list, so
 * both use identical thresholds — including the meter overflow / rollover case where the
 * minimum is greater than the maximum and the plausible range wraps around the overflow point.
 */
export const resolvePlausibilityIndicator = (
    readingValue?: number,
    minimumValue?: number,
    maximumValue?: number,
): PlausibilityIndicator | undefined => {
    if (!readingValue) return undefined;

    const isOverflow = minimumValue !== undefined && maximumValue !== undefined && minimumValue > maximumValue;
    if (isOverflow) {
        if (readingValue > maximumValue! && readingValue < minimumValue!) {
            return LOWER_THAN_EXPECTED;
        }
        return undefined;
    }

    if (maximumValue && readingValue > maximumValue) {
        return HIGHER_THAN_EXPECTED;
    } else if (minimumValue && readingValue < minimumValue) {
        return LOWER_THAN_EXPECTED;
    }

    return undefined;
};

export interface ImplausibleReading {
    readingValue: number;
    minimumValue?: number;
    maximumValue?: number;
}

/**
 * Finds the first implausible recorded reading of a job by inspecting the latest recorded value
 * of each rate task. Returns the raw value plus its expected range so callers can render the
 * shared MeterReadingPlausibilityIndicator component with identical thresholds and appearance.
 * Returns undefined when every recorded value is plausible.
 */
export const resolveImplausibleJobReading = (job: JobType): ImplausibleReading | undefined => {
    for (const task of job.meter?.meterRateTasks ?? []) {
        const lastResult = task.readingResults?.slice(-1)[0];
        if (lastResult?.readingValue === undefined) continue;

        const reading: ImplausibleReading = {
            readingValue: lastResult.readingValue,
            minimumValue: task.readingValueValidation?.minimumValue,
            maximumValue: task.readingValueValidation?.maximumValue,
        };
        if (resolvePlausibilityIndicator(reading.readingValue, reading.minimumValue, reading.maximumValue)) {
            return reading;
        }
    }

    return undefined;
};
